import React, { useEffect, useState } from 'react';
import PremiumButton from './PremiumButton';
import { Subscription, TimeLeft } from '../types/NavigationLink';
import { useTranslations } from '../../localization/useTranslations';

type CountryCode = 'cz' | 'hu' | 'sk';

const SubscriptionBox = () => {
  const translations = useTranslations();
  const isFeatureEnabled = window.isFreeArticlesFeatureEnabled;
  const lockedForFreeUsers = window.lockedForFreeUsers || false;

  const MAX_FREE_ARTICLES = 3;
  const DAYS_COUNT_THRESHOLD = 31;
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [productName, setProductName] = useState<string>(translations.accountDropdown.basicAccount);
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const [remainingFreeArticles, setRemainingFreeArticles] = useState<number>(MAX_FREE_ARTICLES);

  const [buttonUrl, setButtonUrl] = useState('');

  // Get the subscription page slug from window
  const subscriptionSlug = window?.subscriptionPageSlug || 'predplatne';

  // We only want to display the button if the subscription ends in less than 31 days OR the user is not premium
  const hasSubButton = timeLeft?.days || !isPremium;

  // In case the user has multiple subscriptions, we want to display the one that expires the soonest
  const getNearestExpirySub = (subscriptions: Subscription[]): Subscription | null => {
    if (subscriptions.length === 0) {
      return null;
    }

    const nearestExpirySub = subscriptions.reduce((prev, current) =>
      new Date(current.end_time) < new Date(prev.end_time) ? current : prev
    );

    return {
      label: nearestExpirySub.label,
      end_time: nearestExpirySub.end_time,
    };
  };

  const calculateTimeLeft = (earliestExpirySub: Subscription) => {
    const now = new Date();
    const expirationDate = new Date(earliestExpirySub.end_time);
    const diffTime = Math.abs(expirationDate.getTime() - now.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    diffDays <= DAYS_COUNT_THRESHOLD
      ? setTimeLeft({ days: diffDays })
      : setTimeLeft({ months: Math.ceil(diffDays / 31) });
  };

  useEffect(() => {
    const user = window.crm_user;
    if (!user) {
      return;
    }

    if (user.subscriptions && user.subscriptions.length > 0) {
      setIsPremium(true);
      const nearestExpirySub = getNearestExpirySub(user.subscriptions);

      if (nearestExpirySub) {
        setProductName(nearestExpirySub.label);
        calculateTimeLeft(nearestExpirySub);
      }

      setButtonUrl('/' + subscriptionSlug);
    } else {
      setIsPremium(false);

      if (lockedForFreeUsers) {
        // Display locked content message
        setRemainingFreeArticles(0); // No remaining articles if locked
      } else if (isFeatureEnabled || user.metering_article_unlocks?.length > 0) {
        // Show the remaining free articles if the feature is enabled or if the user has already started unlocking articles
        const remainingFreeArticlesCount = user.metering_article_unlocks
          ? Math.max(0, MAX_FREE_ARTICLES - user.metering_article_unlocks.length)
          : MAX_FREE_ARTICLES;

        setRemainingFreeArticles(remainingFreeArticlesCount);
      }

      setButtonUrl('/' + subscriptionSlug);
    }
  }, [isFeatureEnabled, lockedForFreeUsers, subscriptionSlug]);

  // temp cz hardcode
  const renderDaysLeftByCountry = (days: number, country: CountryCode) => {
    if (timeLeft?.days) {
      switch (country) {
        case 'cz':
          if (days === 1) return 'Vyprší za 1 den';
          if (days < 5) return `Vyprší za ${days} dny`;

          return `Vyprší za ${days} dní`;

        default:
          return `Expires in ${days} day(s)`;
      }
    }
  };

  // temp cz hardcode
  const renderMonthsLeftByCountry = (months: number, country: CountryCode) => {
    if (timeLeft?.months) {
      switch (country) {
        case 'cz':
          if (months === 1) return 'Vyprší za 1 měsíc';
          if (months < 5) return `Vyprší za ${months} měsíce`;

          return `Vyprší za ${months} měsíců`;

        default:
          return `Expires in ${months} months`;
      }
    }
  };

  const renderTimeLeft = (country: CountryCode) => {
    if (timeLeft?.days) {
      return renderDaysLeftByCountry(timeLeft?.days, country);
    } else if (timeLeft?.months) {
      return renderMonthsLeftByCountry(timeLeft?.months, country);
    } else {
      return null;
    }
  };

  return (
    <div className="account-dropdown__subscriptions-wrapper">
      <div
        className={`account-dropdown__subscription-wrapper account-dropdown__subscription-wrapper--${
          isPremium ? 'premium' : 'upgrade'
        }`}
      >
        {(!lockedForFreeUsers || isPremium) && <p className="account-dropdown__subscription-label">{productName}</p>}

        <p className="account-dropdown__subscription-description">
          {isPremium
            ? renderTimeLeft('cz')
            : lockedForFreeUsers
              ? 'Aktuálně nemáte žádné předplatné. Pořiďte si skvělé čtění už od 139 Kč měsíčně! Bez limitů a kdykoliv.'
              : remainingFreeArticles < MAX_FREE_ARTICLES && remainingFreeArticles > 0
                ? `Zbývá ${remainingFreeArticles} z 3 Premium článků jako dárek pro vás`
                : 'Aktuálně nemáte žádné předplatné. Pořiďte si skvělé čtění už od 139 Kč měsíčně! Bez limitů a kdykoliv.'}
        </p>

        {hasSubButton && <PremiumButton url={buttonUrl} isPremium={isPremium} />}
      </div>
    </div>
  );
};

export default SubscriptionBox;
